import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { Share2, Facebook, MessageCircle, Copy, Check, Twitter } from 'lucide-react';

/**
 * ShareButtons Component
 * 
 * Provides social sharing functionality for stories, articles, etc.
 * 
 * Props:
 * - url: The URL to share (defaults to current page)
 * - title: The title/text to share
 * - description: Optional description for the share
 * - variant: 'icon' (just icon) or 'full' (icon + text) or 'popover' (share button that opens popover)
 * - size: 'sm', 'md', 'lg'
 * - className: Additional CSS classes
 */
export default function ShareButtons({ 
  url, 
  title, 
  description = '',
  variant = 'popover',
  size = 'md',
  className = ''
}) {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Usa current URL if not provided
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = title || 'Confere este conteúdo no Intimate.pt!';
  
  // Encode for URLs
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const encodedDesc = encodeURIComponent(description);
  
  // Share URLs
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
  const whatsappUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
  
  const handleShare = (platform, shareLink) => {
    window.open(shareLink, '_blank', 'width=600,height=400,noopener,noreferrer');
    setIsOpen(false);
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Native share API (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: description || shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or error - silently ignore
        if (err.name !== 'AbortError') {
          console.error('Share error:', err);
        }
      }
    }
  };
  
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12'
  };
  
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  // Individual share buttons (for inline display)
  const ShareButtonsInline = () => (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Facebook */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('facebook', facebookUrl)}
        className={`${sizeClasses[size]} rounded-full border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500`}
        title="Partilhar no Facebook"
        data-testid="share-facebook"
      >
        <Facebook className={`${iconSizes[size]} text-blue-500`} />
      </Button>
      
      {/* Twitter/X */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('twitter', twitterUrl)}
        className={`${sizeClasses[size]} rounded-full border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500`}
        title="Partilhar no Twitter"
        data-testid="share-twitter"
      >
        <Twitter className={`${iconSizes[size]} text-sky-500`} />
      </Button>
      
      {/* WhatsApp */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('whatsapp', whatsappUrl)}
        className={`${sizeClasses[size]} rounded-full border-green-500/30 hover:bg-green-500/10 hover:border-green-500`}
        title="Partilhar no WhatsApp"
        data-testid="share-whatsapp"
      >
        <MessageCircle className={`${iconSizes[size]} text-green-500`} />
      </Button>
      
      {/* Copy Link */}
      <Button
        variant="outline"
        size="icon"
        onClick={handleCopyLink}
        className={`${sizeClasses[size]} rounded-full border-border hover:bg-muted`}
        title="Copiar link"
        data-testid="share-copy"
      >
        {copied ? (
          <Check className={`${iconSizes[size]} text-green-500`} />
        ) : (
          <Copy className={`${iconSizes[size]} text-muted-foreground`} />
        )}
      </Button>
    </div>
  );
  
  // Popover variant (single button that opens sharing options)
  if (variant === 'popover') {
    // Check if native share is available (mobile)
    const hasNativeShare = typeof navigator !== 'undefined' && navigator.share;
    
    if (hasNativeShare) {
      return (
        <Button
          variant="outline"
          size="icon"
          onClick={handleNativeShare}
          className={`${sizeClasses[size]} rounded-full ${className}`}
          title="Partilhar"
          data-testid="share-button"
        >
          <Share2 className={iconSizes[size]} />
        </Button>
      );
    }
    
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={`${sizeClasses[size]} rounded-full ${className}`}
            title="Partilhar"
            data-testid="share-button"
          >
            <Share2 className={iconSizes[size]} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="end">
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Partilhar</p>
            <div className="flex items-center gap-2">
              {/* Facebook */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare('facebook', facebookUrl)}
                className="h-10 w-10 rounded-full border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500"
                data-testid="share-facebook"
              >
                <Facebook className="w-5 h-5 text-blue-500" />
              </Button>
              
              {/* Twitter/X */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare('twitter', twitterUrl)}
                className="h-10 w-10 rounded-full border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500"
                data-testid="share-twitter"
              >
                <Twitter className="w-5 h-5 text-sky-500" />
              </Button>
              
              {/* WhatsApp */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare('whatsapp', whatsappUrl)}
                className="h-10 w-10 rounded-full border-green-500/30 hover:bg-green-500/10 hover:border-green-500"
                data-testid="share-whatsapp"
              >
                <MessageCircle className="w-5 h-5 text-green-500" />
              </Button>
              
              {/* Copy Link */}
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                className="h-10 w-10 rounded-full"
                data-testid="share-copy"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  // Full variant with labels
  if (variant === 'full') {
    return (
      <div className={`space-y-3 ${className}`}>
        <p className="text-sm font-medium text-foreground">Partilhar</p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => handleShare('facebook', facebookUrl)}
            className="border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500"
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4 mr-2 text-blue-500" />
            Facebook
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleShare('twitter', twitterUrl)}
            className="border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4 mr-2 text-sky-500" />
            Twitter
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleShare('whatsapp', whatsappUrl)}
            className="border-green-500/30 hover:bg-green-500/10 hover:border-green-500"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
            WhatsApp
          </Button>
          
          <Button
            variant="outline"
            onClick={handleCopyLink}
            data-testid="share-copy"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-green-500" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar Link
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }
  
  // Icon variant (default)
  return <ShareButtonsInline />;
}
